import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Spinner,
  useToast,
  Button,
  IconButton,
  HStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useSortBy, useTable } from 'react-table';
import { AddIcon, EditIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card';
import BonusesApi from '../../../../api/bonuses';
import BonusFormModal from './BonusFormModal';

const CRITERIA_LABELS = {
  1: 'חברה ספציפית',
  2: 'מינימום העברות',
  3: 'כל המשתמשים',
};

const BONUS_TYPE_LABELS = {
  1: 'העברה חינמית',
  2: 'כל ההעברות חינם',
};

const BonusModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState(null);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const columns = useMemo(
    () => [
      {
        Header: 'פעולות',
        accessor: 'actions',
        Cell: ({ row }) => (
          <HStack spacing={2}>
            <IconButton
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedBonus(row.original);
                onFormOpen();
              }}
            />
          </HStack>
        ),
      },
      { Header: 'ID', accessor: 'id' },
      { Header: 'שם', accessor: 'name' },
      {
        Header: 'סוג בונוס',
        accessor: 'bonusType',
        Cell: ({ value }) => <Text>{BONUS_TYPE_LABELS[value] || value}</Text>,
      },
      {
        Header: 'קריטריון',
        accessor: 'criteriaType',
        Cell: ({ value }) => <Text>{CRITERIA_LABELS[value] || value}</Text>,
      },
      {
        Header: 'חברה',
        accessor: 'companyId',
        Cell: ({ value }) => (
          <Badge colorScheme={value ? 'blue' : 'green'}>
            {value ? value : 'כל החברות'}
          </Badge>
        ),
      },
      {
        Header: 'מינ׳ העברות',
        accessor: 'minApplicationsThisMonth',
        Cell: ({ value }) => <Text>{value != null ? value : '-'}</Text>,
      },
      { Header: 'תאריך התחלה', accessor: 'startDate' },
      { Header: 'תאריך סיום', accessor: 'endDate' },
      {
        Header: 'פעיל',
        accessor: 'isActive',
        Cell: ({ value }) => (
          <Badge colorScheme={value ? 'green' : 'red'}>
            {value ? 'פעיל' : 'לא פעיל'}
          </Badge>
        ),
      },
    ],
    [onFormOpen]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns, data },
    useSortBy
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await BonusesApi.getBonuses();
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching bonuses:', error);
      toast({ title: 'שגיאה בטעינת בונוסים', status: 'error', duration: 3000, isClosable: true });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchData();
  }, [isOpen]);

  const handleCreate = () => {
    setSelectedBonus(null);
    onFormOpen();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="95vw" maxH="90vh">
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Heading size="lg" color={textColor}>בונוסים חודשיים</Heading>
              <HStack spacing={4} mr="40px">
                <Badge colorScheme="purple" fontSize="md">{data.length} בונוסים</Badge>
                <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={handleCreate}>
                  בונוס חדש
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Card>
              <Flex direction="column" w="100%">
                <Text mb={4} fontSize="sm" color="gray.600">
                  לחץ על עריכה כדי לשנות או לבטל בונוס קיים
                </Text>

                {loading ? (
                  <Flex justify="center" align="center" height="200px">
                    <Spinner size="lg" />
                  </Flex>
                ) : (
                  <Box overflowX="auto">
                    <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
                      <Thead>
                        {headerGroups.map((headerGroup, i) => (
                          <Tr {...headerGroup.getHeaderGroupProps()} key={i}>
                            {headerGroup.headers.map((column, j) => (
                              <Th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                pe="10px"
                                key={j}
                                borderColor={borderColor}
                              >
                                <Flex
                                  justify="space-between"
                                  align="center"
                                  fontSize={{ sm: '10px', lg: '12px' }}
                                  color="gray.400"
                                >
                                  {column.render('Header')}
                                </Flex>
                              </Th>
                            ))}
                          </Tr>
                        ))}
                      </Thead>
                      <Tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                          prepareRow(row);
                          return (
                            <Tr {...row.getRowProps()} key={i}>
                              {row.cells.map((cell, j) => (
                                <Td
                                  {...cell.getCellProps()}
                                  key={j}
                                  fontSize={{ sm: '14px' }}
                                  minW={{ sm: '80px', md: '100px', lg: 'auto' }}
                                  borderColor="transparent"
                                >
                                  <Text color={textColor} fontSize="sm" fontWeight="500">
                                    {cell.render('Cell')}
                                  </Text>
                                </Td>
                              ))}
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {!loading && data.length === 0 && (
                  <Flex justify="center" align="center" height="100px">
                    <Text color="gray.500">אין בונוסים. לחץ על "בונוס חדש" כדי ליצור אחד.</Text>
                  </Flex>
                )}
              </Flex>
            </Card>
          </ModalBody>
        </ModalContent>
      </Modal>

      <BonusFormModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        bonus={selectedBonus}
        onSuccess={fetchData}
      />
    </>
  );
};

export default BonusModal;
